import { useState } from 'react';
import useWindowSize from '@customHooks/useWindowSize';
import { sberPayPaymentWhite } from '@images';
import { Link, useHistory } from '@compat/router';
import dayjs from 'dayjs';
import UIicons from '@ui-kit';
import { createImageErrorHandler } from '@utils/onImageError';
import { declension } from '@utils/declension';
import OrderListItemStatus from '@routes/PersonalArea/routes/components/OrderListItem/components/OrderListItemStatus';
import { Picture, Button, SbpButton } from '@ui';
import useClipboard from '@customHooks/useClipboard';
import RemoveOrderModal from '@components/Modals/RemoveOrderModal';
import PaymentProcess from '@utils/paymentProcess';
import { useStores } from '@root/src/stores';
import icons from '@icons';
import { formatPhoneForLink } from '@utils/formatPhoneForLink';
import { twcls } from '@utils/twcls';
import './index.scss';

const DigitalButton = ({ title, onClick, className, ariaLabel }) => (
  <button
    onClick={onClick}
    className={twcls(
      `h-10
      cursor-pointer
      bg-[var(--ultra-light-grey)]
      border
      border-solid
      border-[var(--main-font-color)]
      rounded-lg
      text-sm
      transition-colors
      hover:text-white
      hover:bg-[var(--dark-red)]
      hover:border-[var(--dark-red)]`,
      className
    )}
    type='button'
    role='button'
    aria-label={ariaLabel}
  >
    {title}
  </button>
);

const { creditCardIncomeOutlined, productCardErrorImg } = UIicons;
const onError = createImageErrorHandler(productCardErrorImg.x3);

const {
  phoneCall: { black: phoneCall },
  copyFile: { darkGrey: copyFile }
} = icons.Outline;

const OrderCard = ({
  item: order,
  repeatOrder,
  changeEditingOrder,
  cancelOrder,
  updatePaymentInfo,
  isMainPage = false
}) => {
  const history = useHistory();
  const { isTablet, isDesktop, isSmallDesktop } = useWindowSize();
  const { modalsStore } = useStores();
  const [isDeleteModalOpened, setIsDeleteModalOpened] = useState(false);

  // console.log('test', item)
  // console.log('test2', dayjs.monthsShort())
  const products = order?.ordersItems;

  const fullOrderPrice = order?.price + order?.delivery_cost;
  const payedWithBonusesAmount = Math.floor(order?.bill_payed) || 0;
  const orderIsPayedFullyWithMoneyOrBonuses =
    order?.paid === 'Y' &&
    Math.floor(Number(order?.paid_total) - fullOrderPrice) >= 0;
  const orderIsPayedPartiallyWithMoneyOrBonuses =
    order?.paid === 'Y'
      ? Math.floor(fullOrderPrice - order?.paid_total) > 0
      : !!payedWithBonusesAmount;
  const orderIsNotPayed = order?.paid === 'N' && !payedWithBonusesAmount;
  const userCanPayAmount =
    orderIsPayedFullyWithMoneyOrBonuses ||
    orderIsPayedPartiallyWithMoneyOrBonuses
      ? (fullOrderPrice - order.paid_total).toFixed(2)
      : null;

  const canUserPayOrder =
    ['2', '3', '4', '5'].includes(order?.statusItem?.id) &&
    order?.payment_id === 10;
  const canUserPaySbp =
    ['2', '3', '4', '5'].includes(order?.statusItem?.id) &&
    order?.payment_id === 19;
  const paymentStatusLabel = () => {
    if (orderIsNotPayed) {
      return <span className='red'>/не&nbsp;оплачен</span>;
    }

    if (orderIsPayedFullyWithMoneyOrBonuses) {
      return <span className='green'>/оплачен</span>;
    }

    if (orderIsPayedPartiallyWithMoneyOrBonuses) {
      return <span className='yellow'>/частично оплачен</span>;
    }
  };

  const countOfSlice = isSmallDesktop ? 4 : isTablet ? 10 : 5;
  const hasMoreProducts = (products?.length || 0) > countOfSlice;
  const visibleProducts = hasMoreProducts
    ? products.slice(0, countOfSlice - 1)
    : products?.slice(0, countOfSlice);
  const hiddenProductsCount = hasMoreProducts
    ? products.length - visibleProducts.length
    : 0;

  const paidOrder = () => {
    PaymentProcess.execute(
      {
        payment_id: order.payment_id,
        id: order.id
      },
      updatePaymentInfo
    );
  };

  const callCourier = () => {
    window.open(`tel:${formatPhoneForLink(order?.courierItem?.cur_phone)}`);
  };

  const { copyPromocode: copyNumber } = useClipboard({
    text: order?.courierItem?.cur_name,
    notificationText: 'Номер авто скопирован!'
  });

  const changePayFeeToCourier = () => {
    modalsStore.openLeaveFeedback({ orderId: +order.id, isOpenTipTab: true });
  };

  const ChangeEstimateShipping = () => {
    modalsStore.openLeaveFeedback({ orderId: +order.id });
  };

  if (!order) {
    return null;
  }
  return (
    <div className='order-card'>
      <Link to={`/cabinet/orders/${order?.id}`} className='title'>
        {!order?.digital && (
          <div className='date'>
            {dayjs(order?.deliveryDate).format('DD MMM YYYY')},{' '}
            {order?.ordBeg?.slice(0, 5)} – {order?.ordEnd?.slice(0, 5)}
          </div>
        )}
        <OrderListItemStatus itemStatusId={order?.status_id} />
      </Link>
      <Link to={`/cabinet/orders/${order?.id}`} className='order-id'>
        Заказ №{order?.id} {paymentStatusLabel()}
      </Link>
      <div className='payment'>
        <div className='status'>
          <Picture
            icon={creditCardIncomeOutlined}
            className='image'
            height='24'
            alt=''
          />{' '}
          Сумма заказа
          {paymentStatusLabel()}
        </div>
        <div className='sum'>{fullOrderPrice} ₽</div>
      </div>
      <div className='order-params'>
        <div className='count'>Заказ:&nbsp;</div>
        <div className='weight'>
          {order?.positionsAmount}{' '}
          {declension(order?.positionsAmount, ['товар', 'товара', 'товаров'])} -{' '}
          {order?.ord_weight} кг
        </div>
      </div>
      {!!visibleProducts?.length && (
        <div className='products-images'>
          {visibleProducts.map(product => (
            <Link
              key={product.id}
              to={`/product/${product.catalogItem?.id}`}
              className='link'
            >
              <img
                alt={product.catalogItem?.title}
                onError={onError}
                className='image'
                src={
                  product.catalogItem?.fileItem?.cacheItem[0]?.filePath ||
                  productCardErrorImg.x3
                }
              />
            </Link>
          ))}
          {hasMoreProducts && (
            <Link
              to={`/cabinet/orders/${order?.id}`}
              className='flex h-12 w-12 min-w-12 items-center justify-center rounded-full bg-[var(--light-grey)] text-sm font-medium text-[var(--ultra-dark-grey)]'
              aria-label={`Ещё ${hiddenProductsCount} ${declension(
                hiddenProductsCount,
                ['товар', 'товара', 'товаров']
              )} в заказе`}
            >
              +{hiddenProductsCount}
            </Link>
          )}
        </div>
      )}
      {order?.status_id === 5 && !!order?.courierItem?.cur_name && (
        <>
          <div className='order-courier'>
            <div className='courier-image-cover'>
              <img
                alt={order?.courierItem?.cur_name}
                onError={onError}
                className='courier-image'
                src={
                  order?.courierItem?.fileItem?.cacheItem[0]?.filePath ||
                  productCardErrorImg.x3
                }
              />
            </div>
            <div className='courier'>
              <div className='courier-title'>Курьер</div>
              <div className='courier-name'>{order?.courierItem?.cur_name}</div>
            </div>
          </div>

          {!!(order?.courierItem?.cur_car || order?.courierItem?.cur_phone) && (
            <div className='buttons-courier'>
              {order?.courierItem?.cur_phone && (
                <Button
                  type='btn&&Icon'
                  text={'Позвонить'}
                  rank='secondary'
                  contentIcons={[{ side: 'left', icon: phoneCall }]}
                  size={'small'}
                  colour='grey'
                  onClick={callCourier}
                />
              )}
              {order?.courierItem?.cur_car && (
                <Button
                  type='btn&&Icon'
                  text={order?.courierItem?.cur_car}
                  rank='secondary'
                  contentIcons={[{ side: 'left', icon: copyFile }]}
                  size={'small'}
                  colour='grey'
                  onClick={copyNumber}
                />
              )}
            </div>
          )}
        </>
      )}
      <div className='buttons'>
        {(order?.canOrderBeRejected || order?.canOrderBeChanged) && (
          <span className='OrderListItemPaymentBtn__btns'>
            {isDeleteModalOpened && (
              <RemoveOrderModal
                closeModal={() => setIsDeleteModalOpened(false)}
                editOrder={
                  order.canOrderBeChanged
                    ? () => {
                        changeEditingOrder();
                        setIsDeleteModalOpened(false);
                      }
                    : undefined
                }
                confirmCancelOrder={async args => {
                  await cancelOrder(args);
                  setIsDeleteModalOpened(false);
                }}
              />
            )}
            {!isMainPage && order?.canOrderBeRejected && (
              <span style={{ width: '100%' }}>
                <Button
                  ariaDescribedby
                  text={'Отменить'}
                  rank='secondary'
                  size={isDesktop ? 'small' : 'extraSmall'}
                  colour='grey'
                  onClick={() => {
                    setIsDeleteModalOpened(true);
                  }}
                />
              </span>
            )}
            {order?.canOrderBeChanged && (
              <span style={{ width: '100%' }}>
                <Button
                  text={'Редактировать'}
                  rank='secondary'
                  size={isDesktop ? 'small' : 'extraSmall'}
                  colour='grey'
                  state={'default'}
                  onClick={changeEditingOrder}
                />
              </span>
            )}
          </span>
        )}

        {(order?.status_id === 11 || order?.status_id === 6) &&
          !!dayjs().diff(dayjs(order?.deliveryDate), 'd') < 7 &&
          !order?.digital && (
            <>
              <Button
                text={'Чаевые курьеру'}
                rank='secondary'
                size={'small'}
                colour='grey'
                onClick={changePayFeeToCourier}
              />

              <Button
                style={{ fontSize: '12px' }}
                text={'Оценить доставку'}
                rank='secondary'
                size={'small'}
                colour='grey'
                onClick={ChangeEstimateShipping}
              />
            </>
          )}
      </div>
      {orderIsPayedFullyWithMoneyOrBonuses && !!order?.digital && (
        <div className='buttons' style={{ gridRow: '6/7' }}>
          <button
            className='BuyBtn__main-btn BuyBtn__main-btn--white order-btn-white'
            onClick={() => history.push(`/cabinet/orders/${order?.id}`)}
          >
            Посмотреть сертификат
          </button>
        </div>
      )}
      {(order?.status_id === 6 || order?.status_id === 11) && (
        <div className='buttons' style={{ gridRow: '7/8' }}>
          <button
            className='BuyBtn__main-btn BuyBtn__main-btn--white order-btn-white'
            onClick={repeatOrder}
          >
            Повторить
          </button>
        </div>
      )}
      {(canUserPaySbp || canUserPayOrder) &&
        !orderIsPayedFullyWithMoneyOrBonuses && (
          <div
            className='buttons-payment'
            style={
              ![2, 6, 11, 3].includes(order?.status_id)
                ? { gridRow: '6/7' }
                : {}
            }
          >
            {canUserPaySbp && (
              <SbpButton
                btnComponent={({ onClick }) =>
                  order.digital ? (
                    <DigitalButton
                      className={
                        'w-full bg-[var(--dark-red)] text-white border-[var(--dark-red)] hover:border-[var(--main-red)] hover:bg-[var(--main-red)]'
                      }
                      title={`Оплатить ${fullOrderPrice} ₽`}
                      onClick={onClick}
                    />
                  ) : undefined
                }
                order={+order.id}
                updatePaymentInfo={updatePaymentInfo}
              />
            )}
            {canUserPayOrder && (
              <div className='OrderListItemPaymentBtn__btns'>
                <button
                  className={
                    orderIsPayedPartiallyWithMoneyOrBonuses
                      ? 'OrderListItemPaymentBtn__btn-partPay'
                      : 'OrderListItemPaymentBtn__btn'
                  }
                  onClick={paidOrder}
                >
                  {orderIsPayedPartiallyWithMoneyOrBonuses
                    ? `Доплатить ${userCanPayAmount} ₽`
                    : 'Оплатить заказ'}
                </button>
                <button
                  className='OrderListItemPaymentBtn__sber-btn'
                  onClick={paidOrder}
                >
                  <Picture
                    className='OrderListItemPaymentBtn__sber-btn__icon'
                    icon={sberPayPaymentWhite}
                    height='18'
                  />
                </button>
              </div>
            )}
          </div>
        )}
    </div>
  );
};

export default OrderCard;
